import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React from "react";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { ModeInsightsProps } from "../../types/insights";
import { yearsAndMonths } from "../../utils";
import ModeInsightsCard from "./ModeInsightsCard";
import StarIcon from "./StarIcon";

export default function TermLengthInsights({ model }: ModeInsightsProps) {
  const c = useCurrencyFormatter();
  const nSimulations = model.results.length;

  const maxTermMonths = Math.max(
    ...model.results.simulations.map((sim) => sim.mortgage.termMonths),
  );
  const minTermMonths = Math.min(
    ...model.results.simulations.map((sim) => sim.mortgage.termMonths),
  );

  const cheapestSimulation = model.results.simulations.reduce(
    (cheapest, sim) => {
      const simTotalCost = sim.totalCostOfMortgage;
      const cheapestTotalCost = cheapest.totalCostOfMortgage;
      return simTotalCost < cheapestTotalCost ? sim : cheapest;
    },
    model.results.simulations[0],
  );

  // Determine if user provided a fixed monthly payment (with calculated interest rate)
  // or a fixed interest rate (with calculated monthly payment)
  const firstSimParams = model.results.simulations[0]?.initialParams;
  const isFixedPaymentMode =
    firstSimParams?.desiredMonthlyPayment !== undefined &&
    firstSimParams?.interestRate === undefined;

  // Calculate metrics for each simulation
  const simulationsWithMetrics = model.results.simulations.map((sim) => {
    const termMonths = sim.mortgage.termMonths;
    const monthlyPayment = sim.mortgage.desiredMonthlyPayment;
    const interestRate = sim.mortgage.interestRate;
    const interestPerYear = sim.totalInterestPaid / (termMonths / 12);
    const savingsPercentage = (
      (sim.totalInterestEarned / sim.totalInterestPaid) *
      100
    ).toFixed(1);
    return {
      sim,
      termMonths,
      monthlyPayment,
      interestRate,
      interestPerYear,
      savingsPercentage,
    };
  });

  // Sort by term length (shortest first)
  const sortedByTerm = [...simulationsWithMetrics].sort(
    (a, b) => a.termMonths - b.termMonths,
  );

  const shortestTerm = sortedByTerm[0];
  const longestTerm = sortedByTerm[sortedByTerm.length - 1];

  // Calculate differences
  const interestSavings =
    longestTerm.sim.totalInterestPaid - shortestTerm.sim.totalInterestPaid;
  const monthlyPaymentDifference =
    shortestTerm.monthlyPayment - longestTerm.monthlyPayment;
  const interestRateDifference =
    longestTerm.interestRate - shortestTerm.interestRate;

  // Check if this is an interest-only mortgage
  const isInterestOnly = model.results.simulations[0]?.interestOnly ?? false;

  // Render different insights based on the mode
  const renderModeSpecificInsight = () => {
    if (isInterestOnly) {
      // Interest-only mode: monthly payment is the same regardless of term
      return (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          With interest-only, monthly payments remain the same regardless of
          term length. Choosing the shortest term saves{" "}
          <strong>{c(interestSavings)}</strong> in interest simply by paying for
          fewer months.
        </Typography>
      );
    } else if (isFixedPaymentMode) {
      // Fixed payment mode: shorter terms require lower interest rates
      return (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Choosing the shortest term saves <strong>{c(interestSavings)}</strong>{" "}
          in interest, but requires an interest rate of{" "}
          <strong>{interestRateDifference.toFixed(2)}%</strong> lower (
          {longestTerm.interestRate.toFixed(2)}% →{" "}
          {shortestTerm.interestRate.toFixed(2)}%).
        </Typography>
      );
    } else {
      // Fixed interest rate mode: shorter terms require higher payments
      return (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Choosing the shortest term saves <strong>{c(interestSavings)}</strong>{" "}
          in interest, but requires{" "}
          <strong>{c(monthlyPaymentDifference)}/month</strong> more in payments.
        </Typography>
      );
    }
  };

  return (
    <ModeInsightsCard>
      <Typography variant="body2" gutterBottom>
        <strong>{nSimulations}</strong> simulations were compared with term
        lengths ranging from <strong>{yearsAndMonths(minTermMonths)}</strong> to{" "}
        <strong>{yearsAndMonths(maxTermMonths)}</strong>.
      </Typography>
      <Typography variant="body2" gutterBottom>
        The most cost-effective term was{" "}
        <strong>
          {yearsAndMonths(cheapestSimulation.mortgage.termMonths)}
        </strong>{" "}
        with a total cost of{" "}
        <strong>{c(cheapestSimulation.totalCostOfMortgage)}</strong>.
      </Typography>
      {renderModeSpecificInsight()}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Term Length</strong>
              </TableCell>
              {!isInterestOnly &&
                (isFixedPaymentMode ? (
                  <TableCell align="right">
                    <strong>Interest Rate</strong>
                  </TableCell>
                ) : (
                  <TableCell align="right">
                    <strong>Monthly Payment</strong>
                  </TableCell>
                ))}
              <TableCell align="right">
                <strong>Total Cost</strong>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Average interest paid per year over the term">
                  <strong>Interest/Year</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <strong>Total Interest</strong>
              </TableCell>
              {model.results.simulations.some(
                (sim) => sim.totalInterestEarned > 0,
              ) && (
                <TableCell align="right">
                  <Tooltip title="% of mortgage interest offset by savings & investments interest">
                    <strong>Savings Offset</strong>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedByTerm.map(
              ({
                sim,
                termMonths,
                monthlyPayment,
                interestRate,
                interestPerYear,
                savingsPercentage,
              }) => (
                <TableRow
                  key={termMonths}
                  sx={{
                    backgroundColor:
                      sim === cheapestSimulation
                        ? "success.lighter"
                        : sim === longestTerm.sim
                          ? "error.lighter"
                          : "inherit",
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {yearsAndMonths(termMonths)}
                    {sim === cheapestSimulation && <StarIcon />}
                  </TableCell>
                  {!isInterestOnly && (
                    <TableCell align="right">
                      {isFixedPaymentMode
                        ? `${interestRate.toFixed(2)}%`
                        : c(monthlyPayment)}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {c(sim.totalCostOfMortgage)}
                  </TableCell>
                  <TableCell align="right">{c(interestPerYear)}</TableCell>
                  <TableCell align="right">
                    {c(sim.totalInterestPaid)}
                  </TableCell>
                  {model.results.simulations.some(
                    (s) => s.totalInterestEarned > 0,
                  ) && (
                    <TableCell align="right">
                      {sim.totalInterestEarned > 0
                        ? `${savingsPercentage}%`
                        : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ModeInsightsCard>
  );
}
